/**
 * Currencies as data (docs/design/currency.md, CUR-2).
 *
 * The SPA never chooses a currency. An amount is shown in the currency the
 * API sent with it (on the row, or on the figure), and a form that asks for
 * one is prefilled with what the API itself would choose when nothing is
 * sent: here the workspace's (R3). Nothing in this file names a currency.
 */
import { useApiQuery } from "./api/hooks";
import { CURRENCY_OPTIONS, currencyName } from "./reference";

/** One amount in one currency, as the API sends it: an exact decimal string. */
export interface CurrencyAmount {
  amount: string;
  currency: string;
}

/**
 * A total that was not added across currencies (R5): the headline in one
 * currency (the workspace's), and every other currency with a non-zero total
 * listed beside it, never converted into the headline.
 */
export interface CurrencyFigure extends CurrencyAmount {
  others: CurrencyAmount[];
}

interface Me {
  workspaceCurrency?: string | null;
}

/**
 * The workspace currency from GET /v1/users/me (readable by every member),
 * through the query the profile page and the locale sync already use, so it
 * costs no second request. Undefined until it has loaded, or when the API
 * did not send it: a caller then leaves the field empty rather than guess.
 */
export function useWorkspaceCurrency(): string | undefined {
  const { data } = useApiQuery<Me>(["me"], "/users/me", { staleTime: 15_000 });
  const code = data?.workspaceCurrency;
  return typeof code === "string" && /^[A-Z]{3}$/.test(code) ? code : undefined;
}

/**
 * The currencies a person can pick, as {value, label}: every books currency,
 * plus `include` when the runtime does not list it (a workspace or record
 * already in a code the picker would otherwise not show).
 */
export function currencyPickerOptions(
  language: string,
  include?: string | null,
): Array<{ value: string; label: string }> {
  const codes =
    include && !CURRENCY_OPTIONS.includes(include)
      ? [...CURRENCY_OPTIONS, include].sort()
      : CURRENCY_OPTIONS;
  return codes.map((code) => {
    const name = currencyName(code, language);
    return { value: code, label: name === code ? code : `${code} · ${name}` };
  });
}

/**
 * Sum decimal strings exactly, grouped by currency: for the few screens that
 * still total a page of rows in the browser (pre-existing, roadmap F8). No
 * floating point: each amount is read as a scaled integer. A value that is
 * not a plain decimal makes that currency's total unknown (undefined), never
 * a wrong number.
 */
export function sumByCurrency(
  rows: ReadonlyArray<{ amount: string | null | undefined; currency: string }>,
): Map<string, string | undefined> {
  const totals = new Map<string, { units: bigint; scale: number } | null>();
  for (const row of rows) {
    if (row.amount === null || row.amount === undefined || row.amount === "")
      continue;
    const parsed = parseDecimal(row.amount);
    const prior = totals.get(row.currency);
    if (prior === null) continue;
    if (!parsed) {
      totals.set(row.currency, null);
      continue;
    }
    if (!prior) {
      totals.set(row.currency, parsed);
      continue;
    }
    const scale = Math.max(prior.scale, parsed.scale);
    totals.set(row.currency, {
      units:
        prior.units * 10n ** BigInt(scale - prior.scale) +
        parsed.units * 10n ** BigInt(scale - parsed.scale),
      scale,
    });
  }
  const out = new Map<string, string | undefined>();
  for (const [currency, total] of totals)
    out.set(currency, total ? toDecimal(total.units, total.scale) : undefined);
  return out;
}

/**
 * `percent` per cent of a decimal string, exactly (a deal's weighted value:
 * expected value × probability / 100, as the summary route computes it).
 * Undefined when either is not a plain number, so the cell shows "—".
 */
export function percentOf(
  value: string | null | undefined,
  percent: number | null | undefined,
): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof percent !== "number" || !Number.isInteger(percent))
    return undefined;
  const parsed = parseDecimal(value);
  if (!parsed) return undefined;
  return toDecimal(parsed.units * BigInt(percent), parsed.scale + 2);
}

function parseDecimal(value: string): { units: bigint; scale: number } | null {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return null;
  const fraction = match[3] ?? "";
  const units = BigInt(`${match[2]}${fraction}`);
  return { units: match[1] ? -units : units, scale: fraction.length };
}

function toDecimal(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units)
    .toString()
    .padStart(scale + 1, "0");
  const whole = digits.slice(0, digits.length - scale);
  const fraction = scale > 0 ? `.${digits.slice(digits.length - scale)}` : "";
  return `${negative ? "-" : ""}${whole}${fraction}`;
}

/**
 * Per-currency totals in the R5 shape: the headline in `headline` ("0" when
 * nothing is in it), every other currency with a non-zero total in `others`,
 * by code. A currency whose total is unknown is listed with an empty amount,
 * which formats as "—".
 */
export function toCurrencyFigure(
  totals: Map<string, string | undefined>,
  headline: string,
): CurrencyFigure {
  const others: CurrencyAmount[] = [];
  for (const [currency, amount] of totals) {
    if (currency === headline) continue;
    if (amount !== undefined && /^-?0+(\.0+)?$/.test(amount)) continue;
    others.push({ currency, amount: amount ?? "" });
  }
  others.sort((a, b) => a.currency.localeCompare(b.currency));
  const own = totals.get(headline);
  return {
    amount: totals.has(headline) ? (own ?? "") : "0",
    currency: headline,
    others,
  };
}
